// src/pages/ReportPage.js
import React, { useMemo, useState, useEffect } from 'react';
import { Form } from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';

import InventoryAgeReport from '../components/InventoryAgeReport';
import OldestVehiclesReport from '../components/OldestVehiclesReport';
import StockAgeBuckets from '../components/StockAgeBuckets';
import StockByModelReport from '../components/StockByModelReport';
import ActiveCustomerSummaryReport from '../components/ActiveCustomerSummaryReport';
import ETAReport from '../components/ETAReport';
import ETAListByRange from '../components/ETAListByRange';
import PaidReport from '../components/PaidReport';

import { useAuth } from '../contexts/AuthContext'; // ⬅️ NEW
import ContractReport from '../components/ContractReport';

const REPORT_OPTIONS = [
  { id: 'paid', label: 'Paid Report', component: PaidReport },
  { id: 'eta', label: 'ETA Report', component: ETAReport },
  { id: 'eta-list', label: 'ETA List by Range', component: ETAListByRange },
  { id: 'inventory-age', label: 'Inventory Age Report', component: InventoryAgeReport },
  { id: 'oldest', label: 'Oldest Vehicles Report', component: OldestVehiclesReport },
  { id: 'stock-age', label: 'Stock Age Buckets', component: StockAgeBuckets },
  { id: 'stock-by-model', label: 'Stock by Model Report', component: StockByModelReport },
  { id: 'active-customer', label: 'Active Customer Summary', component: ActiveCustomerSummaryReport },
  { id: 'contract', label: 'Contract Report', component: ContractReport }
];

// Helper to read role from Firebase custom attributes (unchanged)
function getRole(currentUser) {
  try {
    const raw = currentUser?.reloadUserInfo?.customAttributes;
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.role || null;
  } catch {
    return null;
  }
}

function ReportPage() {
  const { currentUser } = useAuth();
  const role = getRole(currentUser);

  const [searchParams, setSearchParams] = useSearchParams();
  const urlReport = searchParams.get('report') || 'paid';

  // ⬇️ Only allow ETA List by Range for Customer *and* Upfitter roles
  const allowedOptions = useMemo(() => {
    if (role === 'Customer' || role === 'Upfitter') {
      return REPORT_OPTIONS.filter(r => r.id === 'eta-list');
    }
    return REPORT_OPTIONS;
  }, [role]);

  const initialId = allowedOptions.find(o => o.id === urlReport)?.id || allowedOptions[0].id;
  const [reportId, setReportId] = useState(initialId);

  useEffect(() => {
    const desired = searchParams.get('report');
    const validId = allowedOptions.find(o => o.id === desired)?.id || allowedOptions[0].id;
    if (reportId !== validId) setReportId(validId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowedOptions, searchParams]);

  useEffect(() => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('report', reportId);
      return next;
    }, { replace: true });
  }, [reportId, setSearchParams]);

  const Selected = useMemo(() => {
    return (allowedOptions.find(r => r.id === reportId) || allowedOptions[0]).component;
  }, [reportId, allowedOptions]);

  return (
    <div>
      {/* Hide dropdown if only one option (Customer or Upfitter) */}
      {allowedOptions.length > 1 && (
        <div className="d-flex justify-content-end mb-3">
          <Form.Select
            aria-label="Select report"
            value={reportId}
            onChange={(e) => setReportId(e.target.value)}
            className="border-0 shadow-none bg-transparent p-0"
            style={{ width: 'auto' }}
          >
            {allowedOptions.map(opt => (
              <option key={opt.id} value={opt.id}>{opt.label}</option>
            ))}
          </Form.Select>
        </div>
      )}

      <Selected />
    </div>
  );
}
export default ReportPage;